import { StatementAnswerEnum, StatementBooleanAnswerEnum } from 'src/app/models/enums/statement-answer-enum';
import { FormQuestionCodeEnum } from './../../../models/enums/form-question-code-enum';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, QueryList, ViewChildren, ComponentRef, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom, Observable, of, Subject } from 'rxjs';
import { concatMap, filter, finalize, first, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { Permission } from 'src/app/common/enums/permissions';
import { QuestionnaireProcessStep } from 'src/app/common/enums/questionnaire-process-step';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { WorkerFormService } from 'src/app/data/worker-form.service';
import { WorkerService } from 'src/app/data/worker.service';
import { FormQuestion } from 'src/app/models/forms/form-question';
import { WorkerFileDto } from 'src/app/models/worker-file-dto';
import { WorkerFileInfoDto } from 'src/app/models/worker-file-Info-dto';
import { WorkerFormStatementDto } from 'src/app/models/worker-form-statement-dto';
import {
  checkStatementsConflicts,
  foreignWorkerQuestionCodes,
  getAnswerCodeByQuestionCode,
  hasQuestionCode,
  hasStatementFormTypeChanged,
  holidayChildCareQuestionCodes,
  isPit2Statement,
  markStatementsAsSaved,
  ppkRelatedQuestionCodes,
} from './statement-change-detection';
import { StatementDialogContext, StatementDocumentDialogsService } from './statement-document-dialogs.service';
import { StatementFormComponent } from './statement-form/statement-form.component';
import { groupByProperty } from 'src/app/common/utils/group-by-property';
import { WorkerFormBasicPersonalData } from 'src/app/models/worker-form-basic-personal-data';
import { WorkerStatusEnum } from 'src/app/models/enums/WorkerStatusEnum';
import { WorkerAgreementService } from 'src/app/data/worker-agreement.service';
import { KinshipDegreeEnum } from 'src/app/models/enums';
import { WorkerFamilyMemberGridDto } from 'src/app/models/dtos/worker-family-member-grid-dto';
import { StatementFormType } from 'src/app/models/enums/statement-form-type-enum';
import { ClauseDto } from 'src/app/models/dtos/clause-dto';
import { ClauseService } from 'src/app/data/clause.service';
import { ClauseType } from 'src/app/models/enums/clause-type';
import { ExtendedAlertDialogComponent } from 'src/app/shared/messages/extended-alert-dialog/extended-alert-dialog.component';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { FileFormQuestionComponent } from 'src/app/shared/components/forms/file-form-question-component/file-form-question.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { Messages } from 'src/app/common/enums/messages';

@Component({
  selector: 'app-statements-step',
  templateUrl: './statements-step.component.html',
  styleUrls: ['./statements-step.component.scss'],
  standalone: false
})
export class StatementsStepComponent implements OnInit {
  private authService = inject(AuthService);
  private dictionaryService = inject(DictionaryService);
  private workerService = inject(WorkerService);
  private workerFormService = inject(WorkerFormService);
  private workerAgreementService = inject(WorkerAgreementService);
  private spinner = inject(NgxSpinnerService);
  private dialog = inject(MatDialog);
  private statementDialogs = inject(StatementDocumentDialogsService);
  private clauseService = inject(ClauseService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private snackBarService = inject(SnackBarService);

  @Input() workerFormId: number;
  @Input() workerId: number;
  @Input() workerStatements = Array<WorkerFormStatementDto>();
  @Input() fileInfoList = Array<WorkerFileDto>();
  @Input() stepper: MatStepper;
  @Input() workerFormAuthServerUserId: string;
  @Input() workerFormBasicPersonalData: WorkerFormBasicPersonalData;

  @Output() canProceedOnStepClick: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChildren(StatementFormComponent) statementForms: QueryList<StatementFormComponent>;

  formQuestions: FormQuestion[];
  formQuestionsList: FormQuestion[];
  existingQuestions = Array<FormQuestion>();
  files = Array<WorkerFileDto>();
  savedStatements: WorkerFormStatementDto[];
  /**
   * Baseline used to decide which document dialogs are still needed. Kept apart from `savedStatements`, which is a
   * form-rendering input: reassigning it rebuilds the question controls and drops transient flags (has24hoursForApproval).
   */
  private documentBaselineStatements: WorkerFormStatementDto[];
  workerFileTypes = this.dictionaryService.getWorkerFileTypes();
  ppkClauses$: Observable<ClauseDto[]>;

  private readonly unsubscribe$ = new Subject<void>();


  private isWorkerCreated: boolean;
  isRequestPending = false;

  constructor() { }

  get canProceed(): boolean {
    return this.areStatementsValid();
  }

  get statementFormTypeIds(): string[] {
    return !!this.formQuestions ? Object.keys(this.formQuestions) : [];
  }

  ngOnInit() {
    this.stepper.selectionChange
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((event: StepperSelectionEvent) => event.selectedIndex > event.previouslySelectedIndex),
      )
      .subscribe((x) => {
        this.fetchWorkerFormQuestions(x.selectedStep.label as any as QuestionnaireProcessStep);

        if (x.previouslySelectedIndex === QuestionnaireProcessStep.Statements - 1) {
          if (!this.canProceed) {
            setTimeout(() => {
              this.stepper.selectedIndex = x.previouslySelectedIndex;
            });
            return;
          }
          this.submitStatements(true);
        }
      });

    this.savedStatements = this.workerStatements.slice();
    this.documentBaselineStatements = this.savedStatements;

    this.workerService.getWorkerProfile(this.workerId).subscribe((x) => {
      this.isWorkerCreated = x.WorkerStatusId !== WorkerStatusEnum.NotCreated;
    });

    this.ppkClauses$ = this.clauseService.getWorkerInstitutionClause(this.workerId, [ClauseType.PPK]).pipe(shareReplay());
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  private async canOpenPit2Dialog(): Promise<boolean> {
    if (this.isActualPit2StatementAnswerYes() && hasStatementFormTypeChanged(this.documentBaselineStatements, this.workerStatements, StatementFormType.Pit2))
      return await firstValueFrom(this.checkIsUserCanGenerateAgreementRelatedDocument());

    return false;
  }

  private async canOpenHolidayChildCareDialog(): Promise<boolean> {
    return this.isActualHolidayChildCareStatementChanged() && (await firstValueFrom(this.checkIsUserCanGenerateChildCareDocument()));
  }

  private async canOpenPpkCancelationDialog(): Promise<boolean> {
    return this.canGeneratePpkResignation() && (await firstValueFrom(this.checkIsUserCanGenerateAgreementRelatedDocument()));
  }

  private canOpenForeignWorkerDeclarationDialog(): boolean {
    return this.isActualForeignWorkerStatementChanged() && this.hasPerrmissionToGenerateWorkerDocument();
  }

  private isStatementAnsweredYes = (statement: WorkerFormStatementDto): boolean =>
    this.getStatementAnsweredCode(statement) == StatementBooleanAnswerEnum.YES;

  isStatementAnsweredNo = (statement: WorkerFormStatementDto): boolean =>
    statement &&
    this.formQuestionsList.find((fq) => fq.Id === statement.QuestionId).PossibleAnswers?.some(a =>
      statement.Answers.some(answer => answer.AnswerId === a.Id && answer.AnswerCode === StatementBooleanAnswerEnum.NO));

  private getStatementAnsweredCode = (statement: WorkerFormStatementDto): string =>
    this.formQuestionsList
      .find((fq) => {
        return fq.Id === statement?.QuestionId;
      })
      .PossibleAnswers?.find(a => statement.Answers.some(answer => answer.AnswerId === a.Id))?.Code;

  private isStatementListContainsAnswerYesByQuestionCode = (statementsList: WorkerFormStatementDto[], code: string) =>
    statementsList.filter((x) => x.QuestionCode === code).some((x) => this.isStatementAnsweredYes(x));

  private isStatementListContainsAnswerNoByQuestionCode = (statementsList: WorkerFormStatementDto[], code: string) =>
    statementsList.filter((x) => x.QuestionCode === code).some((x) => this.isStatementAnsweredNo(x));

  isActualPit2StatementAnswerYes(): boolean {
    return this.isStatementListContainsAnswerYesByQuestionCode(this.workerStatements, FormQuestionCodeEnum.Pit2);
  };

  canGeneratePpkResignation = (): boolean => {
    let actualStatementsPpkAnswer = this.isStatementListContainsAnswerNoByQuestionCode(this.workerStatements, FormQuestionCodeEnum.PPK);
    let savedStatementsPpkAnswer = this.isStatementListContainsAnswerNoByQuestionCode(this.documentBaselineStatements, FormQuestionCodeEnum.PPK);

    let hasAnyActualStatementConflicted = checkStatementsConflicts(this.workerStatements);
    let hasAnySavedStatementConflicted = checkStatementsConflicts(this.documentBaselineStatements);

    return hasAnyActualStatementConflicted &&
      ((actualStatementsPpkAnswer &&
        actualStatementsPpkAnswer !== savedStatementsPpkAnswer) || (hasAnyActualStatementConflicted != hasAnySavedStatementConflicted));
  };

  async submitStatements(isStepper: boolean): Promise<void> {
    if (this.isRequestPending) {
      return;
    }

    if (this.isFormDisabled() || !this.workerStatements.length) {
      !isStepper && this.stepper.next();
      return;
    }

    if (this.isStudentOlderThan26()) {
      this.showStatementsErrorModal('ConflictStudentOlderThan26Years');
      return;
    }

    this.isRequestPending = true;
    try {
      let submit = true;
      let previewFileClose: Promise<unknown>;
      if (await this.canOpenPit2Dialog()) {
        const result = await this.statementDialogs.openPit2(this.dialogContext, this.workerStatements);
        if (result?.IsSuccess) {
          previewFileClose = this.statementDialogs.previewPit2(this.workerId, result.Value);
          this.markStatementsAsSaved(isPit2Statement);
        }
        submit = result?.IsSuccess ?? false;
      }

      if (submit && (await this.canOpenPpkCancelationDialog())) {
        await previewFileClose;
        const documentId = await this.statementDialogs.openPpkCancellation(this.dialogContext);
        previewFileClose = this.statementDialogs.previewPpkCancellation(this.workerId, documentId);
        submit = !!documentId;
        if (documentId) this.markStatementsAsSaved(hasQuestionCode(ppkRelatedQuestionCodes));
      }

      if (submit && (await this.canOpenHolidayChildCareDialog())) {
        await previewFileClose;
        const documentId = await this.statementDialogs.openHolidayChildCare(this.dialogContext, this.workerStatements);
        this.statementDialogs.previewHolidayChildCare(this.workerId, documentId);
        submit = !!documentId;
        if (documentId) this.markStatementsAsSaved(hasQuestionCode(holidayChildCareQuestionCodes));
      }

      if (submit && this.canOpenForeignWorkerDeclarationDialog()) {
        await previewFileClose;
        const documentId = await this.statementDialogs.openForeignWorkerDeclaration(this.dialogContext, this.workerStatements);
        this.statementDialogs.previewForeignWorkerDeclaration(this.workerId, documentId);
        submit = !!documentId;
        if (documentId) this.markStatementsAsSaved(hasQuestionCode(foreignWorkerQuestionCodes));
      }

      if (submit) {
        this.submit(true, isStepper);
      }
      else {
        this.isRequestPending = false;
      }
    } catch (error) {
      this.isRequestPending = false;
      throw error;
    }
  }

  private get dialogContext(): StatementDialogContext {
    return { workerId: this.workerId, workerFormId: this.workerFormId, personalData: this.workerFormBasicPersonalData };
  }

  /** A signed document must not be offered again when a later dialog in the chain is cancelled. */
  private markStatementsAsSaved(isInScope: (statement: WorkerFormStatementDto) => boolean): void {
    this.documentBaselineStatements = markStatementsAsSaved(this.documentBaselineStatements, this.workerStatements, isInScope);
  }

  submit(nextStep: boolean, isStepper: boolean) {
    this.isRequestPending = true;
    this.stepper.selected.hasError = !this.areStatementsValid();

    this.workerStatements.forEach((statement: WorkerFormStatementDto) => {
      const filesToSend: WorkerFileDto[] = [...this.files, ...this.fileInfoList].filter(
        (file: WorkerFileDto) => file.WorkerFileTypeId == statement.AttachmentTypeId && (!file.WorkerFormStatementId || file.WorkerFormStatementId == statement.Id),
      );

      if (filesToSend.length > 0) {
        statement.Files = filesToSend.map(
          (x: WorkerFileDto) =>
            <WorkerFileInfoDto>{
              WorkerFileId: x?.WorkerFileId,
              OriginalName: x.OriginalName,
              WorkerFileTypeId: x.WorkerFileTypeId,
              FileContent: x.WorkerFileId ? null : x.FileContent,
            },
        );
      }
    });

    this.spinner.show();
    let pendingVerificationShown = false;
    this.workerFormService
      .saveWorkerFormStatements(this.workerFormId, this.workerStatements)
      .pipe(
        first(),
        tap((value) => {
          if (value && value.Errors) {
            this.showStatementsErrorModal(value.Errors);
            this.stepper.selected.completed = false;
            this.stepper.previous();
            this.isRequestPending = false;
            return;
          }

          if (value?.IsPendingVerification && !pendingVerificationShown) {
            this.snackBarService.openSuccessSnackBar(Messages.WorkerFilePendingVerification);
            pendingVerificationShown = true;
          }

          if (this.files.length > 0) {
            this.fileInfoList = this.fileInfoList.concat(this.files);
            this.files = Array<WorkerFileDto>();
          }
        }),
        finalize(() => {
          if (nextStep) {
            !isStepper && this.stepper.next();
            this.savedStatements = this.workerStatements.slice();
            this.documentBaselineStatements = this.savedStatements;
          }
          this.spinner.hide();
          this.isRequestPending = false;
        }),
      )
      .subscribe(
        () => { },
        (err: HttpErrorResponse) => {
          this.stepper.selected.completed = false;
          this.stepper.previous();
          this.isRequestPending = false;
        },
      );
  }

  showStatementsErrorModal(error: string) {
    this.dialog.open(ExtendedAlertDialogComponent, {
      data: {
        textArray: error.split(','),
        translate: true
      },
    });
  }

  onStatementChanged($event: Array<WorkerFormStatementDto>) {
    this.canProceedOnStepClick.emit(this.canProceed);
  }

  private fetchWorkerFormQuestions(questionnaireProcessStep: QuestionnaireProcessStep) {
    if (questionnaireProcessStep !== QuestionnaireProcessStep.Statements) return;
    this.workerService
      .getWorkerFormQuestions(this.workerFormId)
      .pipe(first())
      .subscribe((questions) => {
        this.formQuestions = groupByProperty('StatementFormTypeId')(questions);
        this.formQuestionsList = questions;
        this.changeDetectorRef.detectChanges();
      });
  }

  private areStatementsValid = (): boolean => {
    if (this.workerStatements.length > 0 && (!this.areAllStatementsAnswered() || !this.areFileCheckboxesCheckedIfNoFiles() || !this.statementForms)) return false;

    return this.statementFormTypeIds.every((statementFormTypeId) => this.isLastQuestionOnTheForm(statementFormTypeId));
  };

  private areFileCheckboxesCheckedIfNoFiles = (): boolean => {
    const componentRefs = this.statementForms.toArray().map((x) => x.componentsReferences);
    const flattenComponentRefs = componentRefs.concat.apply([], componentRefs) as ComponentRef<any>[];
    const fileFormsComponentsWithoutFiles = flattenComponentRefs.filter(
      (x) => x.instance instanceof FileFormQuestionComponent && !x.instance.currentlyAddedFiles?.length && !x.instance.fileInfoList?.length,
    );
    return fileFormsComponentsWithoutFiles.length === 0 || fileFormsComponentsWithoutFiles.every((x) => x.instance.has24hoursForApproval);
  };

  private isLastQuestionOnTheForm = (statementFormTypeId: string) =>
    this.workerStatements.some((s) => {
      let a = this.statementForms
        .find((q) => q.statementFormTypeId == statementFormTypeId)
        ?.existingQuestions?.find((q) => q.Id == s.QuestionId)
        ?.PossibleAnswers?.find((a) => s.Answers?.some(answer => answer.AnswerId === a.Id));

      return !!a && !a.NextQuestionId;
    });

  private areAllStatementsAnswered = () => this.workerStatements.some((s) => !!s.Answers.some(a => a.AnswerId) || !!s.Text);

  private isStudentOlderThan26(): boolean {
    const dob = this.workerFormBasicPersonalData?.DateOfBirth;

    if (!dob) {
      return false;
    }

    const age26Date = new Date(dob);
    age26Date.setFullYear(age26Date.getFullYear() + 26);

    if (age26Date > new Date()) {
      return false;
    }

    const hasStudentStatement = this.workerStatements.some(s => s.QuestionCode === FormQuestionCodeEnum.Student);
    
    if (!hasStudentStatement) {
      return false;
    }
    
    return !this.isStatementListContainsAnswerNoByQuestionCode(this.workerStatements, FormQuestionCodeEnum.Student);
  }

  isFormDisabled = () =>
    this.authService.hasPermission(Permission.EditExternalWorkerForm) ? false : this.workerFormAuthServerUserId !== this.authService.authServerUserId;

  private isActualHolidayChildCareStatementChanged = (): boolean => {
    const isHolidayChildcareAnswerChanges = this.getQuestionAnswerChanges(FormQuestionCodeEnum.IsHolidayChildcare);
    const holidayChildcareAnswerChanges = this.getQuestionAnswerChanges(FormQuestionCodeEnum.HolidayChildcare);

    return (
      isHolidayChildcareAnswerChanges.prev !== isHolidayChildcareAnswerChanges.curr ||
      holidayChildcareAnswerChanges.prev !== holidayChildcareAnswerChanges.curr
    );
  };

  private isActualForeignWorkerStatementChanged = (): boolean => {
    const isForeignerAnswerChanges = this.getQuestionAnswerChanges(FormQuestionCodeEnum.IsForeigner);

    if (isForeignerAnswerChanges.prev === StatementAnswerEnum.YES) {
      const futureInPolandAnswerChanges = this.getQuestionAnswerChanges(FormQuestionCodeEnum.FutureInPoland);
      const residentRP183DaysOfTaxYearAnswerChanges = this.getQuestionAnswerChanges(FormQuestionCodeEnum.ResidentRP183DaysOfTaxYear);

      return (
        futureInPolandAnswerChanges.prev !== futureInPolandAnswerChanges.curr ||
        residentRP183DaysOfTaxYearAnswerChanges.prev !== residentRP183DaysOfTaxYearAnswerChanges.curr
      );
    }

    return false;
  };

  private getQuestionAnswerChanges = (code: FormQuestionCodeEnum) => ({
    prev: getAnswerCodeByQuestionCode(this.workerStatements, code),
    curr: getAnswerCodeByQuestionCode(this.documentBaselineStatements, code),
  });

  private checkIsUserCanGenerateAgreementRelatedDocument(includePendingApprovalAgreement: boolean = false): Observable<boolean> {
    return this.workerAgreementService.hasWorkerActiveAgreement(this.workerId).pipe(
      first(),
      map((response) => response.HasActiveAgreement || (includePendingApprovalAgreement && response.HasPendingApprovalAgreement) && this.hasPerrmissionToGenerateWorkerDocument()),
    );
  }

  private checkIsUserCanGenerateChildCareDocument(): Observable<boolean> {
    const familyMemberMaxAge = 14;
    return this.workerService
      .getWorkerFamilyMembers(this.workerId, undefined, 1, undefined, undefined, KinshipDegreeEnum.Child, familyMemberMaxAge)
      .pipe(map((pagedResult) => pagedResult.Results))
      .pipe(map((familyMembers: WorkerFamilyMemberGridDto[]) => familyMembers?.length > 0))
      .pipe(map((hasAnyFamilyMember) => hasAnyFamilyMember && this.hasPerrmissionToGenerateWorkerDocument()));
  }

  private hasPerrmissionToGenerateWorkerDocument(): boolean {
    return this.workerFormAuthServerUserId === this.authService.getAuthServerUserId() || !this.isWorkerCreated;
  }
}
